// client/src/components/pages/LexiStar/Maintenance/EventDialog.tsx (ST-00249)
//
// The three ways to put a truck's time on record (POST /api/lexistar/maintenance/events):
//   Book maintenance     a workshop booking — now, or later (a later start needs an expected end)
//   Report a breakdown   the fast path: truck + what happened, starting now (an earlier start can
//                        be given), the expected end when someone knows it
//   Log a delay          a hold-up — going on now, or already over (start and end)
// The codes are the organisation's (Settings → LexiStar → Maintenance & delay codes), only the
// active ones of the mode's category. The server refuses a second event of the same category
// over the same time on one truck; its message (and the clashing event) is shown here.
// Times are SAST; the shared date-and-time field (desktop list, phone one-tap time chooser).

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { FaTimes } from 'react-icons/fa';
import { SegmentedControl, ThemedSelect } from '../../../../types/commonComponents';
import { PLANNING_TIME_PRESETS } from '../../../common/pickerLogic';
import { DateTimeField, inputClasses } from '../StatusBoard/atoms';
import { createEvent, errorText, overlapOf } from '../../../../services/lexistarMaintenance';
import type { MaintenanceCode, TimeEvent } from '../../../../types/lexistar-maintenance';
import {
  CATEGORY_META,
  MODE_CATEGORY,
  MODE_TEXT,
  eventWhen,
  fmtDuration,
  type BookingMode,
} from './maintenanceLogic';

export interface DialogAsset {
  id: string;
  registration: string | null;
  fleet_no?: string | null;
  /** The owning organisation: only its codes are offered (a global admin sees every organisation's). */
  tenant_id?: number | null;
}

export interface EventDialogProps {
  mode: BookingMode;
  assets: DialogAsset[];
  codes: MaintenanceCode[];
  /** The truck to start with (a control-room chip, a timeline row). */
  assetId?: string | null;
  onClose: () => void;
  onDone: (event: TimeEvent, message: string) => void;
  /** Open the clashing event (the 409's) instead. */
  onOpenEvent?: (event: TimeEvent) => void;
}

type StartChoice = 'now' | 'later';

const DURATIONS: Array<{ label: string; hours: number }> = [
  { label: '1 h', hours: 1 },
  { label: '2 h', hours: 2 },
  { label: '4 h', hours: 4 },
  { label: '8 h', hours: 8 },
  { label: '1 day', hours: 24 },
];

export const assetLabel = (a: DialogAsset): string =>
  [a.registration || 'No registration', a.fleet_no ? `(${a.fleet_no})` : null].filter(Boolean).join(' ');

export default function EventDialog({ mode, assets, codes, assetId = null, onClose, onDone, onOpenEvent }: EventDialogProps) {
  const category = MODE_CATEGORY[mode];
  const meta = CATEGORY_META[category];
  const text = MODE_TEXT[mode];
  const [asset, setAsset] = useState(assetId || (assets.length === 1 ? assets[0]!.id : ''));
  const tenantOf = assets.find((a) => a.id === asset)?.tenant_id ?? null;
  const usable = useMemo(
    () => codes.filter((c) => c.active && c.category === category && (tenantOf === null || c.tenant_id === tenantOf)),
    [codes, category, tenantOf]
  );
  const [code, setCode] = useState('');
  useEffect(() => {
    if (code && !usable.some((c) => c.id === code)) setCode('');
    else if (!code && usable.length === 1) setCode(usable[0]!.id);
  }, [usable, code]);
  const [start, setStart] = useState<StartChoice>('now');
  const [startAt, setStartAt] = useState<Date | null>(null);
  const [expected, setExpected] = useState<Date | null>(null);
  const [over, setOver] = useState(false);
  const [endAt, setEndAt] = useState<Date | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<TimeEvent | null>(null);
  const firstRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Esc closes the dialog — unless it is closing an open date picker or dropdown first.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.react-datepicker-popper, .picker-sheet, .select__menu')) onClose();
    };
    document.addEventListener('keydown', onKey);
    firstRef.current?.querySelector<HTMLElement>('input, button, [tabindex]')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const now = Date.now();
  const startMs = start === 'now' ? now : startAt?.getTime() ?? null;
  const later = start === 'later' && startMs !== null && startMs > now + 60000;
  const problems = {
    asset: asset ? null : 'Choose the truck',
    code: code ? null : usable.length ? 'Choose what it is' : 'There are no active codes for this: add one in Settings → LexiStar → Maintenance & delay codes',
    start: start === 'later' && !startAt ? 'Choose when it starts' : null,
    expected:
      later && !expected
        ? 'A booking that starts later needs an expected end'
        : expected && startMs !== null && expected.getTime() <= startMs
          ? 'The expected end must be after the start'
          : null,
    end: over
      ? !endAt
        ? 'Choose when it ended'
        : endAt.getTime() > now + 60000
          ? 'The end cannot be in the future'
          : startMs !== null && endAt.getTime() <= startMs
            ? 'The end must be after the start'
            : null
      : null,
  };
  const show = (p: string | null) => (touched ? p : null);
  const valid = Object.values(problems).every((p) => p === null);
  const regOf = (id: string) => {
    const a = assets.find((x) => x.id === id);
    return a ? a.registration || 'The truck' : 'The truck';
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    setConflict(null);
    try {
      const created = await createEvent({
        asset_id: asset,
        code_id: code,
        ...(start === 'later' && startAt ? { starts_at: startAt.toISOString() } : {}),
        ...(expected && !over ? { expected_end_at: expected.toISOString() } : {}),
        ...(over && endAt ? { ends_at: endAt.toISOString() } : {}),
        notes: notes.trim() || null,
      });
      onDone(created, `${regOf(asset)} ${text.done}: ${created.label}.`);
      onClose();
    } catch (err) {
      setConflict(overlapOf(err));
      setError(errorText(err, `Could not save. Try again.`));
    } finally {
      setBusy(false);
    }
  };

  const setDuration = (hours: number) => {
    const base = startMs ?? Date.now();
    setExpected(new Date(Math.ceil((base + hours * 3600000) / 300000) * 300000));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto py-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="maintenance-event-title"
        className="bg-surface rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-visible"
        data-mode={mode}
      >
        <form onSubmit={submit} noValidate>
          <div className="flex items-start justify-between gap-4 px-6 pt-5">
            <div className="flex items-start gap-3">
              <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0 ${meta.pill}`} aria-hidden="true">
                <meta.Icon />
              </span>
              <div>
                <h2 id="maintenance-event-title" className="text-lg font-bold text-slate-900">
                  {text.title}
                </h2>
                <p className="text-sm text-slate-500 mt-0.5">
                  {mode === 'breakdown'
                    ? 'The truck counts as broken down from the start until you close it.'
                    : mode === 'delay'
                      ? 'The truck counts as delayed for this time: able to work, but held up.'
                      : 'The truck counts as in planned maintenance for this time.'}
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-1 text-slate-400 hover:text-slate-600">
              <FaTimes />
            </button>
          </div>

          <div className="px-6 py-4 space-y-4" ref={firstRef}>
            <div>
              <label htmlFor="maintenance-asset" className="block text-sm font-medium text-slate-700 mb-1">
                Truck
              </label>
              <ThemedSelect id="maintenance-asset" value={asset} onChange={(ev) => setAsset(String(ev.target.value))} aria-label="Truck" className={inputClasses}>
                <option value="">Choose a truck…</option>
                {assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {assetLabel(a)}
                  </option>
                ))}
              </ThemedSelect>
              {show(problems.asset) && <p className="mt-1 text-xs text-red-600">{problems.asset}</p>}
            </div>

            <div>
              <label htmlFor="maintenance-code" className="block text-sm font-medium text-slate-700 mb-1">
                {text.codeLabel}
              </label>
              <ThemedSelect id="maintenance-code" value={code} onChange={(ev) => setCode(String(ev.target.value))} aria-label={text.codeLabel} className={inputClasses}>
                <option value="">Choose…</option>
                {usable.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </ThemedSelect>
              {show(problems.code) && <p className="mt-1 text-xs text-red-600">{problems.code}</p>}
            </div>

            <fieldset className="space-y-2">
              <legend className="block text-sm font-medium text-slate-700 mb-1">Starts</legend>
              <SegmentedControl<StartChoice>
                options={[
                  { value: 'now', label: 'Now' },
                  { value: 'later', label: mode === 'maintenance' ? 'At a time' : 'Earlier' },
                ]}
                value={start}
                onChange={(val) => {
                  setStart(val);
                  if (val === 'now') setStartAt(null);
                }}
                ariaLabel="When it starts"
              />
              {start === 'later' && (
                <div>
                  <label htmlFor="maintenance-start" className="sr-only">
                    Start
                  </label>
                  <DateTimeField
                    id="maintenance-start"
                    value={startAt}
                    onChange={setStartAt}
                    placeholder="yyyy/mm/dd hh:mm"
                    presets={mode === 'maintenance' ? PLANNING_TIME_PRESETS : undefined}
                    maxDate={mode === 'maintenance' ? null : new Date()}
                  />
                  {show(problems.start) && <p className="mt-1 text-xs text-red-600">{problems.start}</p>}
                </div>
              )}
            </fieldset>

            {mode === 'delay' && (
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={over} onChange={(ev) => setOver(ev.target.checked)} className="h-4 w-4 rounded border-slate-300" />
                It is already over
              </label>
            )}

            {over ? (
              <div>
                <label htmlFor="maintenance-end" className="block text-sm font-medium text-slate-700 mb-1">
                  Ended
                </label>
                <DateTimeField id="maintenance-end" value={endAt} onChange={setEndAt} placeholder="yyyy/mm/dd hh:mm" maxDate={new Date()} />
                {show(problems.end) && <p className="mt-1 text-xs text-red-600">{problems.end}</p>}
              </div>
            ) : (
              <div>
                <label htmlFor="maintenance-expected" className="block text-sm font-medium text-slate-700 mb-1">
                  Expected end {later ? '' : <span className="font-normal text-slate-500">(if known)</span>}
                </label>
                <DateTimeField id="maintenance-expected" value={expected} onChange={setExpected} placeholder="yyyy/mm/dd hh:mm" />
                <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label="Expected to take">
                  {DURATIONS.map((d) => (
                    <button
                      key={d.label}
                      type="button"
                      onClick={() => setDuration(d.hours)}
                      className="px-2.5 py-1 rounded-full text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 ring-1 ring-inset ring-slate-200"
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
                {show(problems.expected) && <p className="mt-1 text-xs text-red-600">{problems.expected}</p>}
                {expected && startMs !== null && expected.getTime() > startMs && (
                  <p className="mt-1 text-xs text-slate-500">About {fmtDuration(expected.getTime() - startMs)}.</p>
                )}
              </div>
            )}

            <div>
              <label htmlFor="maintenance-notes" className="block text-sm font-medium text-slate-700 mb-1">
                Notes <span className="font-normal text-slate-500">(optional)</span>
              </label>
              <textarea
                id="maintenance-notes"
                value={notes}
                maxLength={2000}
                rows={2}
                onChange={(ev) => setNotes(ev.target.value)}
                placeholder={mode === 'breakdown' ? 'Where it is, what the driver says…' : 'Job card, workshop, details…'}
                className={`${inputClasses} resize-y`}
              />
            </div>

            {error && (
              <div role="alert" className="px-3 py-2 text-sm rounded-lg bg-red-50 border border-red-200 text-red-700 space-y-1">
                <p>{error}</p>
                {conflict && (
                  <p className="text-xs text-red-800">
                    {conflict.label} · {eventWhen(conflict)}
                    {onOpenEvent && (
                      <>
                        {' '}
                        <button type="button" onClick={() => onOpenEvent(conflict)} className="font-semibold underline">
                          Open it
                        </button>
                      </>
                    )}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className={`px-5 py-2 text-sm font-semibold text-white rounded-lg shadow-sm disabled:opacity-60 ${
                mode === 'breakdown' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-primary-500 hover:bg-primary-600'
              }`}
            >
              {busy ? 'Saving…' : text.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
